<script lang="ts">
	import { onMount, onDestroy, getContext } from 'svelte'
	import type { Writable } from 'svelte/store'
	import { process_news_badge, process_tooltip } from './utils'
	import type { NewsState, NewsConfig } from './news.types'

	// TODO: in Svelte 5, use createContext with Types
	const news_config: Writable<NewsConfig> = getContext('news_config')
	const user_state: Writable<NewsState | null> = getContext('user_state')

	let news_observer: MutationObserver | null = null
	let tooltip_observer: MutationObserver | null = null
	let async_tooltip_observer: MutationObserver | null = null
	let async_sirius_handler: ((e: Event) => void) | null = null

	onMount(() => {
		const iframe = document.querySelector('iframe')
		if (!iframe) return

		iframe.addEventListener('load', () => {
			const doc = iframe.contentDocument || iframe.contentWindow?.document
			if (!doc) return
			// a document that is still parsing or navigating has no body, and observe() rejects null
			if (!doc.body) return
			// iframe body has logic so we need to know when it's done rendering everything
			// watch for DOM changes and process then
			// there's a race condition between the news badge and the tooltip badge for analytics in particular (because the analysis element depends on further async DB fetches)
			// so currently there are three observers
			// in Svelte 5 this might be solved differently

			news_observer = new MutationObserver(() => {
				process_news(doc)
				news_observer?.disconnect()
			})

			tooltip_observer = new MutationObserver(() => {
				process_tooltips(doc)
				tooltip_observer?.disconnect()
			})

			news_observer.observe(doc.body, {
				childList: true, // fires when children are added or removed
				subtree: true // scan in-depth, not only top-level children
			})

			tooltip_observer.observe(doc.body, {
				childList: true, // fires when children are added or removed
				subtree: true // scan in-depth, not only top-level children
			})
		})
	})

	function process_news(doc: Document) {
		const candidates = doc.querySelectorAll<HTMLElement>('[data-badge-type="news"]')
		;[...candidates].forEach((node: HTMLElement) => {
			const item_id = node.dataset.newsId
			if (item_id) {
				process_news_badge(node, item_id, $user_state?.news, $news_config)
			}
		})
	}

	function process_tooltips(doc: Document) {
		const candidates = doc.querySelectorAll<HTMLElement>('[data-badge-type="tooltip"]')
		if (candidates.length > 0) {
			;[...candidates].forEach((node: HTMLElement) => {
				process_tooltip(node, $user_state?.news, $news_config)
			})
			return
		}
		// if not candidates found, switch to "async" approach
		// HINT: this is a special workaround to receive notifications of
		// async sirius templates finishing their work

		async_tooltip_observer = new MutationObserver(() => {
			process_tooltips(doc)
			async_tooltip_observer?.disconnect()
		})

		async_sirius_handler = (e: Event) => {
			if ((e as CustomEvent).detail.analyticsLoaded) {
				if (!doc.body) {
					window.removeEventListener('asyncSiriusEvent', async_sirius_handler!)
					async_sirius_handler = null
					return
				}
				async_tooltip_observer?.observe(doc.body, {
					childList: true, // fires when children are added or removed
					subtree: true // scan in-depth, not only top-level children
				})
				window.removeEventListener('asyncSiriusEvent', async_sirius_handler!)
				async_sirius_handler = null
			}
		}
		window.addEventListener('asyncSiriusEvent', async_sirius_handler)
	}

	onDestroy(() => {
		news_observer?.disconnect()
		tooltip_observer?.disconnect()
		async_tooltip_observer?.disconnect()
		if (async_sirius_handler) {
			window.removeEventListener('asyncSiriusEvent', async_sirius_handler)
			async_sirius_handler = null
		}
	})
</script>
